import { format } from 'date-fns'
import { StatusType } from 'utility'

export const DASHBOARD_VIEW = {
    cards: 'cards',
    table: 'table',
}

/** Hide per-task score in list/preview until backend Score calculation is fixed. */
export const SHOW_TASK_DASHBOARD_SCORE = false

/** Admin insights (leaderboard + tenant avg KPI) — matches client mockup. */
export const SHOW_TASK_DASHBOARD_ADMIN_SCORE = true

export const DASHBOARD_STATUS_FILTER = {
    all: 'all',
    created: '1',
    inProcess: '2',
    completed: '5',
    down: String(StatusType.Down),
    overdue: 'overdue',
}

export function isDashboardAdmin(user) {
    return Number(user?.RoleId) === 1
}

export function getDefaultDateRange() {
    const today = format(new Date(), 'yyyy-MM-dd')
    return {
        startDate: today,
        endDate: today,
    }
}

/** List API: skip expensive AnswerPreview; load via /FullPreview on hover or eye click. */
export const DASHBOARD_PREVIEW_ANSWER_COUNT = 0

/** Collapsed hover popover shows this many Q&A rows from /FullPreview. */
export const DASHBOARD_TOP_ANSWER_LIMIT = 5

export function getFullPreviewProgress(fullPreview, fallback = 0) {
    if (!fullPreview) return fallback
    const qTotal = fullPreview.QTotal ?? fullPreview.qTotal ?? 0
    const qAnswered = fullPreview.QAnswered ?? fullPreview.qAnswered ?? 0
    if (qTotal > 0) {
        return Math.round((qAnswered / qTotal) * 100)
    }
    return (
        fullPreview.ProgressPct ??
        fullPreview.progressPct ??
        fullPreview.TotalPercentage ??
        fullPreview.totalPercentage ??
        fallback
    )
}

export function flattenFullPreviewAnswers(fullPreview, limit = null) {
    const sections = fullPreview?.Sections ?? fullPreview?.sections ?? []
    const items = sections.flatMap(sec => {
        const questions = sec.Questions ?? sec.Answers ?? []
        return questions.map(q => ({
            SectionName: sec.SectionName ?? sec.sectionName,
            Question: q.Question ?? q.question,
            Answer: q.Answer ?? q.answer,
            Tag: q.Tag ?? q.tag,
            Note: q.Note ?? q.note,
        }))
    })
    if (limit != null && limit >= 0) {
        return items.slice(0, limit)
    }
    return items
}

export function getFullPreviewScoreBySection(fullPreview) {
    const sections = fullPreview?.Sections ?? fullPreview?.sections ?? []
    if (sections.length) {
        return sections.map(sec => ({
            SectionName: sec.SectionName ?? sec.sectionName,
            Pct: sec.Pct ?? sec.pct ?? 0,
        }))
    }
    return fullPreview?.ScoreBySection ?? fullPreview?.scoreBySection ?? []
}

export function getInitials(name = '') {
    return name
        .split(/[\s,]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase()
}

export function getStatusPillClass(statusName = '') {
    const key = statusName.toLowerCase().replace(/\s+/g, '-')
    const map = {
        created: 'created',
        'in-process': 'in-process',
        complete: 'completed',
        completed: 'completed',
        approved: 'approved',
        down: 'down',
        overdue: 'overdue',
        rejected: 'overdue',
    }
    return map[key] || 'created'
}

/** Map dashboard status filter value → pill variant (null = All / neutral) */
export function getStatusFilterVariant(statusValue = '') {
    const map = {
        [DASHBOARD_STATUS_FILTER.created]: 'created',
        [DASHBOARD_STATUS_FILTER.inProcess]: 'in-process',
        [DASHBOARD_STATUS_FILTER.completed]: 'completed',
        [DASHBOARD_STATUS_FILTER.down]: 'down',
        [DASHBOARD_STATUS_FILTER.overdue]: 'overdue',
    }
    return map[statusValue] || null
}

export const DASHBOARD_STATUS_OPTIONS = [
    { value: DASHBOARD_STATUS_FILTER.all, label: 'All' },
    { value: DASHBOARD_STATUS_FILTER.created, label: 'Created' },
    { value: DASHBOARD_STATUS_FILTER.inProcess, label: 'In Process' },
    { value: DASHBOARD_STATUS_FILTER.completed, label: 'Completed' },
    { value: DASHBOARD_STATUS_FILTER.down, label: 'Down' },
    { value: DASHBOARD_STATUS_FILTER.overdue, label: 'Overdue' },
]

/** Quick filter pills from Tasks API facet arrays (Auditor, Customer, etc.). */
export function buildFacetQuickFilterOptions(
    facets,
    facetKey,
    totalCount = 0,
    allValue = '',
) {
    const facetItems = facets?.[facetKey] ?? facets?.[facetKey.toLowerCase()] ?? []
    const options = [
        {
            value: allValue,
            label: 'All',
            count: totalCount,
        },
    ]
    facetItems.forEach(facet => {
        const value = String(facet.Key ?? facet.key ?? '')
        const label = (facet.Label ?? facet.label ?? '').trim() || value
        const count = facet.Count ?? facet.count ?? 0
        if (!value) return
        options.push({ value, label, count })
    })
    return options
}

/** Quick status pills driven by Tasks API `Facets.Status` (not Stats API). */
export function buildStatusQuickFilterOptions(facets, totalCount = 0) {
    return buildFacetQuickFilterOptions(
        facets,
        'Status',
        totalCount,
        DASHBOARD_STATUS_FILTER.all,
    )
}

export function buildAuditorQuickFilterOptions(facets, totalCount = 0) {
    return buildFacetQuickFilterOptions(facets, 'Auditor', totalCount)
}

export function buildCustomerQuickFilterOptions(facets, totalCount = 0) {
    return buildFacetQuickFilterOptions(facets, 'Customer', totalCount)
}

export const FACET_QUICK_FILTER_TOP_N = 3

/** Collapse facet pills to All + top N (by count); always keep the active selection visible. */
export function getVisibleFacetQuickFilterOptions(
    options = [],
    { expanded = false, activeValue = '', topN = FACET_QUICK_FILTER_TOP_N } = {},
) {
    if (!options.length) {
        return { visible: [], hasMore: false, hiddenCount: 0 }
    }

    const [allOption, ...rest] = options
    if (expanded || rest.length <= topN) {
        return { visible: options, hasMore: rest.length > topN, hiddenCount: 0 }
    }

    const sorted = [...rest].sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    const top = sorted.slice(0, topN)
    const activeStr = String(activeValue ?? '')

    if (activeStr && !top.some(opt => String(opt.value) === activeStr)) {
        const activeOpt = sorted.find(opt => String(opt.value) === activeStr)
        if (activeOpt) {
            top.push(activeOpt)
        }
    }

    const visible = allOption ? [allOption, ...top] : top
    const hiddenCount = rest.length - top.length

    return {
        visible,
        hasMore: hiddenCount > 0,
        hiddenCount,
    }
}

export function getFacetQuickFilterCount(
    facetKey,
    value,
    facets,
    totalCount = 0,
    allValue = '',
) {
    if (!value || value === allValue) {
        return totalCount
    }
    const facetItems = facets?.[facetKey] ?? facets?.[facetKey.toLowerCase()] ?? []
    const match = facetItems.find(
        facet => String(facet.Key ?? facet.key) === String(value),
    )
    return match?.Count ?? match?.count ?? 0
}

export function getStatusQuickFilterCount(statusValue, facets, totalCount = 0) {
    return getFacetQuickFilterCount(
        'Status',
        statusValue,
        facets,
        totalCount,
        DASHBOARD_STATUS_FILTER.all,
    )
}

/** Created By dropdown options from Tasks API `Facets.Auditor`. */
export function buildAuditorFacetOptions(facets) {
    const auditors = facets?.Auditor ?? facets?.auditor ?? []
    return auditors.map(facet => {
        const value = String(facet.Key ?? facet.key ?? '')
        const label = (facet.Label ?? facet.label ?? '').trim() || value
        const count = facet.Count ?? facet.count ?? 0
        return {
            value,
            text: `${label} (${count})`,
        }
    }).filter(opt => opt.value)
}

export function getScorePillClass(score) {
    if (score == null) return ''
    if (score >= 90) return 'score-good'
    if (score >= 85) return 'score-ok'
    if (score >= 75) return 'score-warn'
    return 'score-bad'
}

export function getDueClass(dueState) {
    if (dueState === 'ok') return 'ok'
    if (dueState === 'warn') return 'warn'
    if (dueState === 'bad') return 'bad'
    return ''
}

export function formatTaskDate(value) {
    if (!value) return ''
    try {
        const d = new Date(value)
        return format(d, 'yyyy-MM-dd HH:mm')
    } catch {
        return String(value)
    }
}

export function formatTaskDateShort(value) {
    if (!value) return ''
    try {
        return format(new Date(value), 'yyyy-MM-dd')
    } catch {
        return String(value)
    }
}

export function buildStatusQuery(statusFilter) {
    if (
        !statusFilter ||
        statusFilter === DASHBOARD_STATUS_FILTER.all ||
        statusFilter === DASHBOARD_STATUS_FILTER.overdue
    ) {
        return null
    }
    return statusFilter
}

export function filterOverdueTasks(items) {
    return (items || []).filter(
        item => item.DueState === 'bad' || item.DueState === 'warn',
    )
}

/** Client-side status filter for quick pills when the main API filter is All. */
export function filterTasksByStatus(items, statusFilter) {
    if (
        !statusFilter ||
        statusFilter === DASHBOARD_STATUS_FILTER.all
    ) {
        return items || []
    }
    if (statusFilter === DASHBOARD_STATUS_FILTER.overdue) {
        return filterOverdueTasks(items)
    }
    return (items || []).filter(
        item => String(item.Status) === String(statusFilter),
    )
}

export function getTaskAuditorId(task) {
    return (
        task?.AuditorUserId ??
        task?.UserId ??
        task?.CreatedBy ??
        task?.AuditorId ??
        task?.auditorId ??
        ''
    )
}

export function getTaskCustomerId(task) {
    return task?.CustomerId ?? task?.customerId ?? ''
}

/** Client-side auditor filter for quick pills when Created By is All. */
export function filterTasksByAuditor(items, auditorId) {
    if (!auditorId) {
        return items || []
    }
    return (items || []).filter(
        item => String(getTaskAuditorId(item)) === String(auditorId),
    )
}

/** Client-side customer filter for quick pills when Customer is All. */
export function filterTasksByCustomer(items, customerId) {
    if (!customerId) {
        return items || []
    }
    return (items || []).filter(
        item => String(getTaskCustomerId(item)) === String(customerId),
    )
}

export function usesClientStatusQuickFilter(appliedStatus) {
    return (
        !appliedStatus || appliedStatus === DASHBOARD_STATUS_FILTER.all
    )
}

export function usesClientAuditorQuickFilter(appliedAuditorId) {
    return !appliedAuditorId
}

export function usesClientCustomerQuickFilter(appliedCustomerId) {
    return !appliedCustomerId
}

const AUDIT_GUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** TaskId → audit GUID from legacy GET InspectionTask (Inspection?AuditId= value) */
let legacyInspectionRouteByTaskId = {}

function toAuditGuid(value) {
    if (value == null || value === '') return ''
    const text = String(value).trim().replace(/^\{|\}$/g, '')
    return AUDIT_GUID_RE.test(text) ? text : ''
}

function isAuditGuid(value) {
    return Boolean(toAuditGuid(value))
}

/**
 * Legacy GET InspectionTask stored the real audit GUID in TemplateId.
 * Task List no longer loads that API — Dashboard AuditTemplateId is the source of truth.
 * Helpers kept for any residual callers.
 */
export function setLegacyInspectionTaskRoutes(tasks) {
    const next = {}
    const rows = tasks || []
    rows.forEach(row => {
        if (row?.TaskId == null) return
        const auditId = toAuditGuid(row.AuditTemplateId) || toAuditGuid(row.TemplateId)
        if (auditId) {
            next[row.TaskId] = auditId
        }
    })
    legacyInspectionRouteByTaskId = next
}

export function getLegacyInspectionRouteId(taskId) {
    if (taskId == null) return ''
    return legacyInspectionRouteByTaskId[taskId] || ''
}

export function getTaskInspectionRouteId(task) {
    // GET Inspection?AuditId= needs the audit instance GUID.
    // Catalog TemplateId is numeric — only use TemplateId when it is itself a GUID
    // (legacy InspectionTask stored the audit GUID there).
    if (!task) return ''
    const candidates = [
        task.AuditTemplateId,
        task.auditTemplateId,
        task.AuditTemplateID,
        task.AuditUniqueId,
        task.auditUniqueId,
        task.AuditId,
        task.auditId,
        task.TemplateId,
        task.templateId,
        getLegacyInspectionRouteId(task.TaskId),
    ]
    for (let i = 0; i < candidates.length; i += 1) {
        const guid = toAuditGuid(candidates[i])
        if (guid) return guid
    }
    return ''
}

/** Same AuditId (GUID) used by InspectionReport?AuditId= */
export function getTaskReportId(task) {
    return getTaskInspectionRouteId(task)
}

export function getTaskInspectionNumericId(task) {
    if (!task) return null
    const id = task.InspectionId ?? task.Id
    if (id == null || id === '') return null
    if (isAuditGuid(String(id))) return null
    return id
}

/**
 * True CompletedReportId from the task payload when present.
 * Do NOT fall back to InspectionId — that is AuditId and 404s on Approval/Report/{id}.
 */
export function getTaskCompletedReportId(task) {
    if (!task) return null
    const id =
        task.CompletedReportId ??
        task.completedReportId ??
        task.E_CompletedReportId ??
        task.CompletedReportID
    if (id == null || id === '') return null
    return id
}

/** Numeric AuditId / InspectionId used to look up CompletedReportId */
export function getTaskAuditIdForApproval(task) {
    if (!task) return null
    const id =
        task.AuditId ??
        task.auditId ??
        getTaskInspectionNumericId(task)
    if (id == null || id === '') return null
    if (isAuditGuid(String(id))) return null
    return id
}

/** True/false when task payload already has approval info; null if unknown */
export function getTaskIsApprovedFlag(task) {
    if (!task) return null
    if (task.IsApproved === true || task.isApproved === true) return true
    if (task.IsApproved === false || task.isApproved === false) return false

    const status = String(
        task.ApprovalStatus ??
            task.approvalStatus ??
            task.ReportApprovalStatus ??
            '',
    ).toLowerCase()
    if (status === 'approved' || status === '2') return true
    if (status === 'pending' || status === '1') return false
    return null
}

export function getTaskTemplateName(task) {
    return task?.TemplateName || task?.templateName || ''
}

const TASK_LABEL_VALUE_FIELD_KEYS = [
    'TemplateData',
    'templateData',
    'TaskDisplayFields',
    'taskDisplayFields',
    'DisplayFields',
    'displayFields',
    'AdditionalInfo',
    'additionalInfo',
    'AdditionalInformation',
    'additionalInformation',
    'CustomerAdditionalInfo',
    'customerAdditionalInfo',
]

const LABEL_VALUE_CHILD_KEYS = [
    'Items',
    'items',
    'Data',
    'data',
    'Fields',
    'fields',
    'Values',
    'values',
]

function parseMaybeJson(value) {
    if (typeof value !== 'string') return value
    const trimmed = value.trim()
    if (!trimmed || !['[', '{'].includes(trimmed[0])) return value

    try {
        return JSON.parse(trimmed)
    } catch {
        return value
    }
}

function getTrimmedText(value) {
    if (value == null) return ''
    return String(value).trim()
}

function normalizeLabelValueEntry(entry) {
    if (!entry || typeof entry !== 'object') return null

    const label = getTrimmedText(
        entry.Label ??
            entry.label ??
            entry.FieldName ??
            entry.fieldName ??
            entry.Name ??
            entry.name,
    )
    const value = getTrimmedText(
        entry.Value ??
            entry.value ??
            entry.FieldValue ??
            entry.fieldValue ??
            entry.DisplayValue ??
            entry.displayValue,
    )

    if (!label || !value) return null

    return {
        label,
        value,
        slot: Number(entry.Slot ?? entry.slot ?? 0) || 0,
    }
}

function getLabelValueEntries(source) {
    const parsed = parseMaybeJson(source)

    if (Array.isArray(parsed)) {
        return parsed
    }

    if (!parsed || typeof parsed !== 'object') {
        return []
    }

    const nestedEntries = LABEL_VALUE_CHILD_KEYS.flatMap(key =>
        getLabelValueEntries(parsed[key]),
    )

    return normalizeLabelValueEntry(parsed)
        ? [parsed, ...nestedEntries]
        : nestedEntries
}

function getTaskLabelValueDisplay(task) {
    if (!task) return null

    const keyedEntries = TASK_LABEL_VALUE_FIELD_KEYS.flatMap(key =>
        getLabelValueEntries(task[key]),
    )
    const discoveredEntries = Object.values(task).flatMap(getLabelValueEntries)
    const entries = [...keyedEntries, ...discoveredEntries]
        .map(normalizeLabelValueEntry)
        .filter(Boolean)
        .sort((a, b) => a.slot - b.slot)

    return entries[0] || null
}

export function getTaskCustomerLabel(task) {
    return task?.label2 || task?.Address || task?.CustomerName || ''
}

export function getTaskCustomerDisplay(task) {
    const labelValueDisplay = getTaskLabelValueDisplay(task)
    if (labelValueDisplay) return labelValueDisplay

    // Empty ReportFields (and no other label/value fields) → show N/A, not CustomerName ("Self")
    return { label: 'Customer', value: 'N/A' }
}

export function getTaskCreatedByName(task) {
    return task?.CreatedByName || task?.createdByName || task?.AuditorName || ''
}

export function getTaskTitleSubtitle(task) {
    const title = task?.Title || task?.label1 || ''
    const templateName = getTaskTemplateName(task)
    const customer = getTaskCustomerLabel(task)
    if (!title || title === templateName || title === customer) {
        return ''
    }
    return title
}

export function normalizeDashboardTask(task) {
    if (!task) return task
    const auditTemplateId =
        task.AuditTemplateId ??
        task.auditTemplateId ??
        task.AuditTemplateID ??
        null
    const inspectionId =
        task.InspectionId ?? task.inspectionId ?? task.Id ?? null
    const templateId = task.TemplateId ?? task.templateId ?? null
    const resolvedAuditGuid =
        toAuditGuid(auditTemplateId) || toAuditGuid(templateId) || null
    const rawReadOnly = task.IsReadOnly ?? task.isReadOnly
    const hasExplicitReadOnly =
        rawReadOnly !== undefined && rawReadOnly !== null && rawReadOnly !== ''
    return {
        ...task,
        // Real audit instance GUID only — numeric catalog TemplateId stays off this field.
        AuditTemplateId: resolvedAuditGuid,
        InspectionId: inspectionId,
        TemplateId: templateId,
        // Keep explicit API flag; omit coercion to false so FE can fall back by owner.
        ...(hasExplicitReadOnly
            ? {
                  IsReadOnly:
                      rawReadOnly === true ||
                      rawReadOnly === 1 ||
                      rawReadOnly === '1' ||
                      String(rawReadOnly).toLowerCase() === 'true',
              }
            : {}),
        CreatedByName:
            task.CreatedByName ??
            task.createdByName ??
            task.AuditorName ??
            task.auditorName ??
            '',
        AuditorName:
            task.AuditorName ??
            task.auditorName ??
            task.CreatedByName ??
            task.createdByName ??
            '',
    }
}

export function normalizeDashboardTasks(items) {
    return (items || []).map(normalizeDashboardTask)
}

export function mapDashboardTaskForActions(task) {
    return normalizeDashboardTask(task)
}

export function mapDashboardTaskForEdit(task) {
    if (!task) return task
    const normalized = normalizeDashboardTask(task)
    const auditRouteId = getTaskInspectionRouteId(normalized)
    // POST InspectionTask update — TemplateId must be audit GUID (legacy list value).
    return {
        TaskId: normalized.TaskId,
        TemplateId: auditRouteId || normalized.TemplateId,
        UserId: normalized.AuditorUserId ?? normalized.UserId,
        CustomerId: normalized.CustomerId,
        CustomerName: normalized.CustomerName,
        TaskDue: normalized.TaskDue,
        InspectionDate: normalized.TaskDate || normalized.CreatedOn || normalized.InspectionDate,
        Status: normalized.Status,
        StatusName: normalized.StatusName,
        InspectionId: getTaskInspectionNumericId(normalized) ?? normalized.InspectionId,
        SendEmailReminder: normalized.SendEmailReminder,
    }
}

export function isTaskCompleted(item) {
    return (
        item?.Status === StatusType.Completed ||
        item?.StatusName?.toLowerCase() === 'completed'
    )
}

/**
 * Numeric E_AuditCustomersTemplate.unique_id for GET /TemplateSetting/{templateId}.
 * Dashboard tasks may expose this under several field names; GUID TemplateId is ignored.
 */
export function getTaskTemplateSettingId(task) {
    if (!task) return null
    const candidates = [
        task.OriginalTemplateId,
        task.originalTemplateId,
        task.TemplateUniqueId,
        task.templateUniqueId,
        task.CustomerTemplateId,
        task.customerTemplateId,
        task.TemplateNumericId,
        task.templateNumericId,
    ]
    const fromNamedFields = candidates
        .map(candidate => Number(candidate))
        .find(n => Number.isFinite(n) && n > 0)
    if (fromNamedFields) return fromNamedFields

    const templateId = task.TemplateId ?? task.templateId
    if (templateId != null && !isAuditGuid(String(templateId))) {
        const n = Number(templateId)
        if (Number.isFinite(n) && n > 0) return n
    }
    return null
}

export { isPostCompleteEditBlocked } from 'utility/template-setting-utility'

/** Status label + pill variant; approved completed reports show APPROVED (not COMPLETED). */
export function getTaskStatusDisplay(task, isReportApproved = false) {
    if (isReportApproved && isTaskCompleted(task)) {
        return { label: 'Approved', variant: 'approved' }
    }
    if (Number(task?.Status) === StatusType.Down) {
        return { label: 'Down', variant: 'down' }
    }
    const label = task?.StatusName || ''
    return { label, variant: getStatusPillClass(label) }
}

export function getTaskActionLabel(item) {
    return isTaskCompleted(item) ? 'View Report' : 'Resume Inspection'
}

export function getTaskTableActionLabel(item) {
    return isTaskCompleted(item) ? 'View' : 'Resume'
}

export function normalizeStatusMix(raw = {}) {
    return {
        Created: Number(raw.Created ?? raw.created ?? 0) || 0,
        InProcess:
            Number(raw.InProcess ?? raw.inProcess ?? raw.In_Process ?? 0) || 0,
        Completed: Number(raw.Completed ?? raw.completed ?? 0) || 0,
        Overdue: Number(raw.Overdue ?? raw.overdue ?? 0) || 0,
    }
}

export function normalizeLeaderboardRow(row = {}) {
    const userId =
        row.UserId ??
        row.userId ??
        row.AuditorUserId ??
        row.auditorUserId ??
        row.Id ??
        row.id

    return {
        UserId: userId,
        UserName:
            row.UserName ??
            row.userName ??
            row.AuditorName ??
            row.auditorName ??
            row.Name ??
            row.name ??
            row.FullName ??
            row.fullName ??
            '',
        TaskCount:
            Number(
                row.TaskCount ??
                    row.taskCount ??
                    row.Tasks ??
                    row.tasks ??
                    row.Count ??
                    row.count ??
                    0,
            ) || 0,
        AvgScore: (() => {
            const raw =
                row.AvgScore ??
                row.avgScore ??
                row.AverageScore ??
                row.averageScore ??
                row.Score ??
                row.score
            if (raw == null || raw === '') return null
            const n = Number(raw)
            return Number.isFinite(n) ? Math.round(n) : null
        })(),
    }
}

export function normalizeLeaderboard(raw) {
    if (!Array.isArray(raw)) return []
    return raw
        .map(normalizeLeaderboardRow)
        .filter(row => row.UserId != null || row.UserName)
        .sort((a, b) => (b.TaskCount || 0) - (a.TaskCount || 0))
}

export function normalizeDashboardResponse(data) {
    if (!data) return data
    const meta = data.MetaData ?? data.metaData ?? {}
    const rawStatusMix = data.StatusMix ?? data.statusMix ?? {}
    const rawLeaderboard = data.Leaderboard ?? data.leaderboard ?? []
    return {
        scope: data.Scope ?? data.scope,
        window: data.Window ?? data.window,
        kpis: data.Kpis ?? data.kpis,
        statusMix: normalizeStatusMix(rawStatusMix),
        leaderboard: normalizeLeaderboard(rawLeaderboard),
        totalCount:
            data.TotalCount ??
            data.totalCount ??
            meta.Total ??
            meta.total ??
            0,
        pageNumber:
            data.PageNumber ??
            data.pageNumber ??
            meta.CurrentPage ??
            meta.currentPage,
        pageSize:
            data.PageSize ??
            data.pageSize ??
            meta.PerPage ??
            meta.perPage,
        items: data.Items ?? data.items ?? [],
        facets: data.Facets ?? data.facets ?? null,
    }
}

export function buildKpiTiles(stats, isAdmin) {
    const kpis = stats?.kpis || {}
    const total = kpis.TotalTasks ?? 0
    const inProcess = kpis.InProcess ?? 0
    const completed = kpis.Completed ?? 0
    const overdue = kpis.Overdue ?? 0
    const avgScore = kpis.AvgScore ?? 0
    const completedToday = kpis.CompletedToday ?? 0
    const myOpenTasks = kpis.MyOpenTasks ?? inProcess

    const pct = (n) => (total ? Math.round((n / total) * 100) : 0)

    if (isAdmin) {
        const activeAuditors = (stats?.leaderboard || []).length
        const tiles = [
            {
                label: 'Tasks (all users)',
                value: total,
                delta: '',
                accent: 'blue',
            },
            {
                label: 'In process',
                value: inProcess,
                delta: `${pct(inProcess)}% of total`,
                accent: 'amber',
            },
            {
                label: 'Completed',
                value: completed,
                delta: `${pct(completed)}% of total`,
                accent: 'green',
            },
            {
                label: 'Overdue',
                value: overdue,
                delta: `${pct(overdue)}% of total`,
                accent: 'red',
            },
            {
                label: 'Avg score (tenant)',
                value: `${avgScore}%`,
                delta: 'Last 30d',
                accent: 'purple',
            },
            {
                label: 'Active auditors',
                value: activeAuditors,
                delta: 'Top performers',
                accent: 'slate',
            },
        ]
        return SHOW_TASK_DASHBOARD_ADMIN_SCORE
            ? tiles
            : tiles.filter(tile => tile.label !== 'Avg score (tenant)')
    }

    const tiles = [
        {
            label: 'My open tasks',
            value: myOpenTasks,
            delta: `${overdue} overdue`,
            accent: 'amber',
        },
        {
            label: 'Completed today',
            value: completedToday,
            delta: 'Today',
            accent: 'green',
            up: completedToday > 0,
        },
        {
            label: 'Overdue',
            value: overdue,
            delta: 'Action needed',
            accent: 'red',
        },
        {
            label: 'My avg score',
            value: `${avgScore}%`,
            delta: 'Last 30d',
            accent: 'blue',
        },
    ]
    return SHOW_TASK_DASHBOARD_SCORE
        ? tiles
        : tiles.filter(tile => tile.label !== 'My avg score')
}

const STATUS_MIX_COLORS = {
    created: '#1565c0',
    inProcess: '#e65100',
    completed: '#2e7d32',
    overdue: '#c62828',
}

export function buildStatusMixChartData(statusMix = {}) {
    const normalized = normalizeStatusMix(statusMix)
    const entries = [
        {
            name: 'Created',
            value: normalized.Created,
            color: STATUS_MIX_COLORS.created,
        },
        {
            name: 'In Process',
            value: normalized.InProcess,
            color: STATUS_MIX_COLORS.inProcess,
        },
        {
            name: 'Completed',
            value: normalized.Completed,
            color: STATUS_MIX_COLORS.completed,
        },
        {
            name: 'Overdue',
            value: normalized.Overdue,
            color: STATUS_MIX_COLORS.overdue,
        },
    ]
    const total = entries.reduce((s, e) => s + e.value, 0)
    return { entries, total }
}
